import React, { createContext, useContext, useState, useCallback } from 'react';
import logger from '../utils/logger';


const TOUR_STORAGE_PREFIX = 'shiftsmart_tour_completed_';

const GuidedTourContext = createContext();

/**
 * Check whether a given user has already completed the guided tour.
 * Persistence is handled through localStorage keyed by userId so each user
 * gets their own first-login flag without requiring backend changes.
 */
const hasCompletedTour = (userId) => {
  if (!userId) return true; // Default to "completed" when no userId
  try {
    return localStorage.getItem(`${TOUR_STORAGE_PREFIX}${userId}`) === 'true';
  } catch {
    return false;
  }
};

const markTourCompleted = (userId) => {
  if (!userId) return;
  try {
    localStorage.setItem(`${TOUR_STORAGE_PREFIX}${userId}`, 'true');
  } catch (error) {
    logger.warn('Failed to persist tour completion flag:', error);
  }
};

const clearTourCompleted = (userId) => {
  if (!userId) return;
  try {
    localStorage.removeItem(`${TOUR_STORAGE_PREFIX}${userId}`);
  } catch (error) {
    logger.warn('Failed to clear tour completion flag:', error);
  }
};

export function GuidedTourProvider({ children }) {
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsTourActive(true);
  }, []);

  const endTour = useCallback((userId) => {
    setIsTourActive(false);
    setCurrentStep(0);
    markTourCompleted(userId);
  }, []);

  const skipTour = useCallback((userId) => {
    setIsTourActive(false);
    setCurrentStep(0);
    markTourCompleted(userId);
  }, []);

  const nextStep = useCallback((totalSteps) => {
    setCurrentStep((prev) => {
      if (prev < totalSteps - 1) return prev + 1;
      return prev;
    });
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);

  const goToStep = useCallback((stepIndex) => {
    setCurrentStep(stepIndex);
  }, []);

  const resetTour = useCallback((userId) => {
    clearTourCompleted(userId);
    setCurrentStep(0);
    setIsTourActive(false);
  }, []);

  return (
    <GuidedTourContext.Provider
      value={{
        isTourActive,
        currentStep,
        startTour,
        endTour,
        skipTour,
        nextStep,
        prevStep,
        goToStep,
        resetTour,
        hasCompletedTour,
        markTourCompleted,
      }}
    >
      {children}
    </GuidedTourContext.Provider>
  );
}

export function useGuidedTour() {
  const context = useContext(GuidedTourContext);
  if (!context) {
    throw new Error('useGuidedTour must be used within a GuidedTourProvider');
  }
  return context;
}

export default GuidedTourContext;
